import { Dispatch, JSX, SetStateAction, useEffect, useRef, useState } from 'react';
import DatePicker, { DatePickerProps } from 'react-datepicker';
import Container from '@ui/container/container';
import timezoneMap from './timezone-short-name-map.json' with { type: 'json' };

import BasicSelectInput from '@ui/select/react-select/basic-select-input';
import { isObject } from 'underscore';
import { DateRange } from './types';
import { getAdjustedDate, getDefaultEnd, getDefaultStart, getUserTimezone } from './utils';
import OptionalComponent from '@ui/optional-component/optional-component';
/* Don't use css modules, it doesn't work well with react-datepicker */
import './date-range-picker.css';
import Popover from '@ui/popover/popover';
import DateRangePickerIncrementalOptions from './helpers/date-range-picker-incrememntal-options';
import { TRequiresTimeChange } from '@/types/session.types';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import { useTimePicker } from './incremental-options-utils/use-time-picker';
import { FilterOptionOption } from 'react-select';
import DateRangeDayPicker from './date-range-day-picker';

type DateRangePickerHorizontalProps = {
  defaultTimezone?: keyof typeof timezoneMap;
  defaultValue?: DateRange;
  /** only pass in value if you want the component to be controlled */
  value?: DateRange;
  onChange?: (data: DateRange) => void;
  datePickerProps?: DatePickerProps;
  timePickerProps?: DatePickerProps;
  renderFooter?: (data: {
    startDate: Date | null;
    endDate: Date | null;
    setStartDate: Dispatch<SetStateAction<Date | null>>;
    setEndDate: Dispatch<SetStateAction<Date | null>>;
  }) => JSX.Element | null;
  selectSingleDate?: boolean;
  hideTimezone?: boolean;
  overflowClass?: string;
  requiresTimeChange?: TRequiresTimeChange | null;
};

const DateRangePickerHorizontal = ({
  defaultValue,
  value,
  onChange: propsChange,
  datePickerProps,
  timePickerProps,
  renderFooter,
  selectSingleDate = false,
  hideTimezone = false,
  overflowClass,
  requiresTimeChange,
}: DateRangePickerHorizontalProps) => {
  const [startDate, setStartDate] = useState<Date | null>(getDefaultStart(defaultValue, value));
  const [endDate, setEndDate] = useState<Date | null>(getDefaultEnd(defaultValue, value));
  const [invalidEndTime, setInvalidEndTime] = useState<boolean>(false);
  const [selectedTimezone, setSelectedTimezone] = useState<keyof typeof timezoneMap>(
    (defaultValue?.selectedTimezone || getUserTimezone()) as keyof typeof timezoneMap
  );

  const startTimeContainerRef = useRef<HTMLDivElement | null>(null);
  const startOptionsRef = useRef<HTMLDivElement | null>(null);

  const endTimeContainerRef = useRef<HTMLDivElement | null>(null);
  const endOptionsRef = useRef<HTMLDivElement | null>(null);

  const [startTimeSelectOpen, setStartTimeSelectOpen] = useTimePicker(startTimeContainerRef, startOptionsRef);
  const [endTimeSelectOpen, setEndTimeSelectOpen] = useTimePicker(endTimeContainerRef, endOptionsRef);

  useEffect(() => {
    if (value?.startDate && selectSingleDate) {
      setStartDate(new Date(value.startDate));
    } else if (value?.startDate && value.endDate) {
      setStartDate(new Date(value.startDate));
      setEndDate(new Date(value.endDate));
    }
  }, [value, selectSingleDate]);

  const handleDateDropdownOptionSelect = (date: Date, isEnd: boolean) => {
    if (isEnd) {
      setEndDate(date);
      const isValid = !!date && !!startDate && date > startDate;
      setInvalidEndTime(!isValid);

      if (isValid && propsChange) {
        propsChange({
          startDate: startDate?.getTime() || null,
          endDate: date.getTime(),
          selectedTimezone,
        });
      }

      setEndTimeSelectOpen(false);
    } else {
      setStartDate(date);
      let newEndDate = endDate;
      if (date && !newEndDate) {
        newEndDate = new Date(date);
        newEndDate.setMinutes(date.getMinutes() + 60);
        setEndDate(newEndDate);
      }

      const isValid = !!date && !!newEndDate && date < newEndDate;
      setInvalidEndTime(!isValid);

      if (isValid && propsChange) {
        propsChange({
          startDate: date.getTime(),
          endDate: newEndDate?.getTime() || null,
          selectedTimezone,
        });
      }

      setStartTimeSelectOpen(false);
    }
  };

  return (
    <Container
      height="auto"
      background="var(--gray-800)"
      className="custom-date-range-wrapper flex flex-row"
      borderRadius="12px"
    >
      <DateRangeDayPicker
        value={value}
        onChange={propsChange}
        datePickerProps={datePickerProps}
        selectSingleDate={selectSingleDate}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedTimezone={selectedTimezone}
        setInvalidEndTime={setInvalidEndTime}
      />

      <Container
        height="192px"
        margin="auto 10px auto 16px"
        className={overflowClass || 'overflow-auto'}
        background="var(--gray-900)"
        borderRadius="12px"
      >
        <Container height="64px" className="flex-row hereian" padding="15px 18px 15px 16px" ref={startTimeContainerRef}>
          <div className="flex-column flex-1">
            <div className="flex-1 S2 flex ai-center">{selectSingleDate ? '' : 'Start '}Time</div>
          </div>
          <Popover
            forcePosition="bottom-center"
            open={startTimeSelectOpen}
            ignoreClasses={['.react-datepicker-time__input']}
            width="auto"
            closeOnTabAway={false}
            onOpen={() => setEndTimeSelectOpen(false)}
            renderTriggerButton={() => (
              <Container data-component="DatePicker Container">
                <DatePicker
                  // this spread is technically correct, but react-datepicker has a poorly written type definition when it comes to user accessibility
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  {...(timePickerProps as any)}
                  disabled={!startDate}
                  calendarClassName={`custom-time-picker ${timePickerProps?.calendarClassName || ''}`}
                  selected={
                    startDate ? new Date(startDate.toLocaleString('en-US', { timeZone: selectedTimezone })) : null
                  }
                  onChange={getAdjustedDate(selectedTimezone, (adjustedStartDate: Date | null) => {
                    setStartDate(adjustedStartDate);
                    // user has set a start time but no end time, default to 60 minutes after start time
                    let newEndDate = endDate;
                    if (adjustedStartDate && !newEndDate) {
                      newEndDate = new Date(adjustedStartDate);
                      newEndDate.setMinutes(adjustedStartDate.getMinutes() + 60);
                      setEndDate(newEndDate);
                    }

                    const isValid = !!adjustedStartDate && !!newEndDate && adjustedStartDate < newEndDate;
                    setInvalidEndTime(!isValid);

                    if (isValid && propsChange) {
                      propsChange({
                        startDate: adjustedStartDate.getTime(),
                        endDate: newEndDate?.getTime() || null,
                        selectedTimezone,
                      });
                    }
                  })}
                  inline={timePickerProps?.inline ?? true}
                  showTimeSelectOnly={true}
                  timeIntervals={timePickerProps?.timeIntervals ?? 30}
                  showTimeInput
                  locale={''}
                />
              </Container>
            )}
          >
            <Container className="flex flex-row">
              <Container
                ref={startOptionsRef}
                className="flex flex-column no-scrollbar"
                maxHeight="200px"
                width="250px"
                style={{
                  overflowY: 'scroll',
                }}
                background="var(--gray-900)"
                borderRadius="12px"
              >
                <DateRangePickerIncrementalOptions
                  startDate={startDate}
                  endDate={endDate}
                  onSelect={handleDateDropdownOptionSelect}
                  requiresTimeChange={requiresTimeChange}
                  timeZone={selectedTimezone}
                />
              </Container>
            </Container>
          </Popover>
        </Container>

        <OptionalComponent display={!selectSingleDate}>
          <Container height="64px" className="flex-row" padding="15px 18px 15px 16px" ref={endTimeContainerRef}>
            <div className="flex-1 S2 flex ai-center">End Time</div>
            <Popover
              forcePosition="bottom-center"
              open={endTimeSelectOpen}
              ignoreClasses={['.react-datepicker-time__input']}
              width="170px"
              closeOnTabAway={false}
              onOpen={() => setStartTimeSelectOpen(false)}
              renderTriggerButton={() => (
                <Container>
                  <DatePicker
                    // this spread is technically correct, but react-datepicker has a poorly written type definition when it comes to user accessibility
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    {...(timePickerProps as any)}
                    disabled={!endDate}
                    calendarClassName={`custom-time-picker ${timePickerProps?.calendarClassName || ''} ${invalidEndTime ? 'custom-time-picker-invalid-time' : ''}`}
                    selected={
                      endDate ? new Date(endDate.toLocaleString('en-US', { timeZone: selectedTimezone })) : null
                    }
                    onChange={getAdjustedDate(selectedTimezone, (date) => {
                      setEndDate(date);

                      const isValid = !!date && !!startDate && date > startDate;
                      setInvalidEndTime(!isValid);

                      if (isValid && propsChange) {
                        propsChange({
                          startDate: startDate?.getTime() || null,
                          endDate: date.getTime(),
                          selectedTimezone,
                        });
                      }
                    })}
                    inline={timePickerProps?.inline ?? true}
                    showTimeSelectOnly={true}
                    timeIntervals={timePickerProps?.timeIntervals ?? 5}
                    showTimeInput
                  />
                </Container>
              )}
            >
              <Container
                ref={endOptionsRef}
                className="no-scrollbar"
                maxHeight="200px"
                //width="250px"
                style={{
                  overflowY: 'scroll',
                }}
                background="var(--gray-900)"
                borderRadius="12px"
              >
                <DateRangePickerIncrementalOptions
                  startDate={startDate}
                  endDate={endDate}
                  isEnd
                  onSelect={handleDateDropdownOptionSelect}
                  requiresTimeChange={requiresTimeChange}
                  timeZone={selectedTimezone}
                />
              </Container>
            </Popover>
          </Container>
        </OptionalComponent>

        <OptionalComponent display={!hideTimezone}>
          <Container background={'var(--gray-800)'} height="2px" />

          <Container height="64px" className="flex-row" padding="15px 4px 15px 16px" gap="12px">
            <div className="flex-1 S2 flex ai-center">Timezone</div>
            <Container
              style={{ color: 'var(--gray-400)' }}
              width="170px"
              minWidth="170px"
              className="flex-1 C3 flex ai-center"
            >
              <BasicSelectInput
                styles={reactSelectAdminStyles({
                  container: () => ({ height: '32px', width: '158px' }),
                  control: (_, props) => ({
                    height: '32px',
                    paddingLeft: '0',
                    borderColor:
                      // '!important' is used here because it is used in the parent
                      // see: src/general-ui/select/react-select/react-select-styles.ts:31
                      props.isFocused || props.menuIsOpen ? 'var(--gray-300) !important' : 'var(--gray-600) !important',
                  }),
                  menuList: () => ({
                    backgroundColor: 'var(--gray-800)',
                    maxHeight: '200px',
                    fontSize: '11px',
                    width: '160px',
                    scrollbarWidth: 'none',
                  }),
                  option: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }),
                })}
                value={selectedTimezone}
                placeholder={timezoneMap[selectedTimezone] || 'Los Angeles'}
                filterOption={(option: FilterOptionOption<unknown>, inputValue) => {
                  const { label } = option;
                  return label.toLowerCase().includes(inputValue.toLowerCase());
                }}
                options={Object.entries(timezoneMap)
                  .map((option) => ({
                    label: option[1],
                    value: option[0],
                  }))
                  .toSorted((a, b) => a.label.localeCompare(b.label))}
                onChange={(selected) => {
                  if (isObject(selected) && 'value' in selected) {
                    setSelectedTimezone(selected.value as keyof typeof timezoneMap);
                    if (startDate && endDate) {
                      propsChange?.({
                        startDate: startDate ? startDate.getTime() : null,
                        endDate: endDate ? endDate.getTime() : null,
                        selectedTimezone: selected.value as string,
                      });
                    }
                    localStorage.setItem('last-timezone', selected.value as string);
                  }
                }}
                type="inline"
              />
            </Container>
          </Container>
        </OptionalComponent>
      </Container>

      {renderFooter?.({ startDate, endDate, setStartDate, setEndDate })}
    </Container>
  );
};

export default DateRangePickerHorizontal;
